import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import { FormattedMessage } from 'react-intl';
import SortableTable from 'components/ui/SortableTable';
import { Link } from 'react-router-dom';

import Clock from 'assets/rebrand/icons/clock.svg';
import CancelJourney from 'assets/rebrand/icons/cancelJourney.svg';
import MinusInCircle from 'assets/rebrand/icons/minusInCircle.svg';
import TickInCircle from 'assets/rebrand/icons/tickInCircle.svg';
import WarningTriangle from 'assets/rebrand/icons/warningTriangle.svg';

import { Status, StatusImage } from './BookingSearchTable.styles';

import { bookingStatus } from 'types/booking';

import { colours } from 'themes/default';

const bookingStatusIcons = {
  [bookingStatus.PROVISIONAL]: Clock,
  [bookingStatus.ON_HOLD]: WarningTriangle,
  [bookingStatus.TEMPORARILY_ON_HOLD]: WarningTriangle,
  [bookingStatus.CONFIRMED]: TickInCircle,
  [bookingStatus.REFUNDED]: CancelJourney,
  [bookingStatus.CANCELLED]: MinusInCircle
};

const compareStatus = (a, b) => {
  const bookingStatusOrder = {
    CONFIRMED: 10,
    ON_HOLD: 20,
    TEMPORARILY_ON_HOLD: 30,
    PROVISIONAL: 40,
    REFUNDED: 50,
    CANCELLED: 60
  };

  return bookingStatusOrder[a] - bookingStatusOrder[b];
};

const compareMinors = (a, b) => Number(a) - Number(b);

const columnsForAssistance = [
  {
    key: 'reference',
    intlId: 'bookingSearchResults.reference',
    format: (reference) => <Link to={`/manage/bookings/${reference}`}>{reference}</Link>
  },
  {
    key: 'masterReference',
    intlId: 'bookingSearchResults.masterReference',
    format: (masterReference) => masterReference && <Link to={`/bookings?reference=${masterReference}`}>{masterReference}</Link>
  },
  {
    key: 'status',
    intlId: 'bookingSearchResults.status',
    format: (status) => {
      const icon = bookingStatusIcons[status];
      return (
        <Status status={status}>
          <StatusImage src={icon} />
          <span>{status}</span>
        </Status>
      );
    },
    compare: compareStatus
  },
  {
    key: 'trains',
    intlId: 'bookingSearchResults.trains',
    format: (items) => (items || []).map((item) => <div key={item}>{item}</div>)
  },
  {
    key: 'coachAndSeatForGivenDay',
    intlId: 'bookingSearchResults.coachAndSeatForGivenDay',
    format: (items) => (items || []).map((item) => <div key={item}>{item}</div>)
  },
  {
    key: 'specialAssistance',
    intlId: 'bookingSearchResults.specialAssistance',
    format: (items) => (items || []).filter((item, i, items) => items.indexOf(item) === i).map((item) => <div key={item}>{item}</div>)
  },
  {
    key: 'firstName',
    intlId: 'bookingSearchResults.firstName'
  },
  {
    key: 'lastName',
    intlId: 'bookingSearchResults.lastName'
  },
  {
    key: 'departingOn',
    intlId: 'bookingSearchResults.departingOn'
  },
  {
    key: 'departingFrom',
    intlId: 'bookingSearchResults.departingFrom'
  },
  {
    key: 'departingTo',
    intlId: 'bookingSearchResults.departingTo'
  }
];

const columnsForCoachAndSeat = [
  {
    key: 'reference',
    intlId: 'bookingSearchResults.reference',
    format: (reference) => <Link to={`/manage/bookings/${reference}`}>{reference}</Link>
  },
  {
    key: 'status',
    intlId: 'bookingSearchResults.status',
    format: (status) => {
      const icon = bookingStatusIcons[status];
      return (
        <Status status={status}>
          <StatusImage src={icon} />
          <span>{status}</span>
        </Status>
      );
    },
    compare: compareStatus
  },
  {
    key: 'coachAndSeatProducts',
    intlId: 'bookingSearchResults.coachAndSeatProduct',
    format: (items) =>
      (items ?? []).map((item) => (
        <div key={`${item.carriage}-${items.seatNumber}`}>
          {item.carriage} / {item.seatNumber}
        </div>
      ))
  },
  {
    key: 'coachAndSeatPassengers',
    intlId: 'bookingSearchResults.coachAndSeatPassenger',
    format: (items) =>
      (items || []).map((item) => (
        <div key={item.id}>
          {item.firstName} {item.lastName}
        </div>
      ))
  },
  {
    key: 'departingOn',
    intlId: 'bookingSearchResults.departingOn'
  },
  {
    key: 'departingFrom',
    intlId: 'bookingSearchResults.departingFrom'
  },
  {
    key: 'departingTo',
    intlId: 'bookingSearchResults.departingTo'
  }
];

const columnsForTickets = [
  {
    key: 'reference',
    intlId: 'bookingSearchResults.reference',
    format: (reference) => <Link to={`/manage/bookings/${reference}`}>{reference}</Link>
  },
  {
    key: 'status',
    intlId: 'bookingSearchResults.status',
    format: (status) => {
      const icon = bookingStatusIcons[status];
      return (
        <Status status={status}>
          <StatusImage src={icon} />
          <span>{status}</span>
        </Status>
      );
    },
    compare: compareStatus
  },
  {
    key: 'passengers',
    intlId: 'bookingSearchResults.passenger',
    format: (passengers) => `${passengers?.length > 0 ? `${passengers[0].passenger.firstName} ${passengers[0].passenger.lastName}` : '--'}`
  },
  {
    key: 'coachAndSeatProducts',
    intlId: 'bookingSearchResults.coachAndSeatProduct',
    format: () => '--'
  },
  {
    key: 'passengerCount',
    intlId: 'bookingSearchResults.passengerCount'
  },
  {
    key: 'departingOn',
    intlId: 'bookingSearchResults.departingOn'
  },
  {
    key: 'departingFrom',
    intlId: 'bookingSearchResults.departingFrom'
  },
  {
    key: 'departingTo',
    intlId: 'bookingSearchResults.departingTo'
  }
];

const columnsForLuggage = [
  {
    key: 'reference',
    intlId: 'bookingSearchResults.reference',
    format: (reference) => <Link to={`/manage/bookings/${reference}`}>{reference}</Link>
  },
  {
    key: 'status',
    intlId: 'bookingSearchResults.status',
    format: (status) => {
      const icon = bookingStatusIcons[status];
      return (
        <Status status={status}>
          <StatusImage src={icon} />
          <span>{status}</span>
        </Status>
      );
    },
    compare: compareStatus
  },
  {
    key: 'trains',
    intlId: 'bookingSearchResults.trains',
    format: (items) => (items || []).map((item) => <div key={item}>{item}</div>)
  },
  {
    key: 'coachSeat',
    intlId: 'bookingSearchResults.coachAndSeatProduct'
  },
  {
    key: 'extraItems',
    intlId: 'bookingSearchResults.extra',
    format: (items) => (items || []).map((item) => <div key={item.itemRef}>{item.productName}</div>)
  },
  {
    key: 'itemCoach',
    intlId: 'bookingSearchResults.itemCoach',
    format: (items) => (items || []).map((item) => <div key={item.itemRef}>{item.itemCoach ?? '--'}</div>)
  },
  {
    key: 'trainSeal',
    intlId: 'bookingSearchResults.trainSeal',
    format: (items) => (items || []).map((item) => <div key={item.itemRef}>{item.trainSeal ?? '--'}</div>)
  },
  {
    key: 'itemSeal',
    intlId: 'bookingSearchResults.itemSeal',
    format: (items) => (items || []).map((item) => <div key={item.itemRef}>{item.itemSeal ?? '--'}</div>)
  },
  {
    key: 'customer',
    intlId: 'bookingSearchResults.customer',
    format: (items) => Object.keys(items || []).map((key) => <div key={key}>{items[key] ?? '--'}</div>)
  },
  {
    key: 'departingOn',
    intlId: 'bookingSearchResults.departingOn'
  },
  {
    key: 'departingFrom',
    intlId: 'bookingSearchResults.departingFrom'
  },
  {
    key: 'departingTo',
    intlId: 'bookingSearchResults.departingTo'
  }
];

const columns = [
  {
    key: 'reference',
    intlId: 'bookingSearchResults.reference',
    format: (reference) => <Link to={`/manage/bookings/${reference}`}>{reference}</Link>
  },
  {
    key: 'masterReference',
    intlId: 'bookingSearchResults.masterReference',
    format: (masterReference) => masterReference && <Link to={`/bookings?reference=${masterReference}`}>{masterReference}</Link>
  },
  {
    key: 'agentOffice',
    intlId: 'bookingDetails.agentOffice'
  },
  {
    key: 'status',
    intlId: 'bookingSearchResults.status',
    format: (status) => {
      const icon = bookingStatusIcons[status];
      return (
        <Status status={status}>
          <StatusImage src={icon} />
          <span>{status}</span>
        </Status>
      );
    },
    compare: compareStatus
  },
  {
    key: 'paymentDue',
    intlId: 'bookingSearchResults.paymentDue',
    compare: (lhs, rhs) => new Date(lhs).valueOf() - new Date(rhs).valueOf()
  },
  {
    key: 'specialAssistance',
    intlId: 'bookingSearchResults.specialAssistance',
    format: (items) => (items || []).filter((item, i, items) => items.indexOf(item) === i).map((item) => <div key={item}>{item}</div>)
  },
  {
    key: 'minorsAmount',
    intlId: 'bookingSearchResults.minorsAmount',
    compare: compareMinors
  },
  {
    key: 'firstName',
    intlId: 'bookingSearchResults.firstName'
  },
  {
    key: 'lastName',
    intlId: 'bookingSearchResults.lastName'
  },
  {
    key: 'passengerCount',
    intlId: 'bookingSearchResults.passengerCount'
  },
  {
    key: 'departingOn',
    intlId: 'bookingSearchResults.departingOn'
  },
  {
    key: 'departingFrom',
    intlId: 'bookingSearchResults.departingFrom'
  },
  {
    key: 'departingTo',
    intlId: 'bookingSearchResults.departingTo'
  },
  {
    key: 'returningOn',
    intlId: 'bookingSearchResults.returningOn'
  }
];

const BookingSearchTable = ({
  bookings,
  history: { replace },
  location: { pathname, search },
  defaultSortKey,
  removeColumns = [],
  columnsAssistances = false,
  columnsCoachAndSeat = false,
  columnsTicket = false,
  columnsLuggage = false
}) => {
  const queryParams = querystring.parse(search.substr(1));
  const { sortKey: initialSortKey = defaultSortKey || 'status', asc = 'true' } = querystring.parse(search);

  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortOrderIsAscending, setSortOrderIsAscending] = useState(asc === 'true');

  useEffect(() => {
    const query = {
      ...queryParams,
      sortKey,
      asc: sortOrderIsAscending ? 'true' : 'false'
    };

    if (query.sortKey !== queryParams.sortKey || query.asc !== queryParams.asc) {
      replace(`${pathname}?${querystring.stringify(query)}`);
    }
  }, [pathname, queryParams, replace, sortKey, sortOrderIsAscending]);

  const updateUrl = ({ sortKey: updatedSortKey, sortOrderIsAscending: updatedSortOrderIsAscending }) => {
    if (updatedSortKey !== sortKey) {
      setSortKey(updatedSortKey);
    }
    if (updatedSortOrderIsAscending !== sortOrderIsAscending) {
      setSortOrderIsAscending(updatedSortOrderIsAscending);
    }
  };

  const data = bookings.map(
    ({
      reference,
      masterReference,
      status,
      agentOffice,
      paymentDue,
      minorsAmount,
      firstName,
      lastName,
      passengerCount,
      passengers,
      departingFrom,
      departingOn,
      departingTo,
      returningOn,
      returningFrom,
      returningTo,
      specialAssistance,
      trains,
      coachAndSeatForGivenDay,
      coachAndSeatProducts,
      coachAndSeatPassengers,
      // Fields for luggage
      coachSeat,
      extraItems,
      itemCoach,
      trainSeal,
      itemSeal,
      customer
    }) => ({
      key: reference,
      reference,
      masterReference,
      status,
      agentOffice,
      paymentDue,
      minorsAmount,
      firstName,
      lastName,
      passengerCount,
      passengers,
      departingOn,
      departingFrom,
      departingTo,
      returningOn,
      returningFrom,
      returningTo,
      specialAssistance,
      trains,
      coachAndSeatForGivenDay,
      coachAndSeatProducts,
      coachAndSeatPassengers,
      // Fields for luggage
      coachSeat,
      extraItems,
      itemCoach,
      trainSeal,
      itemSeal,
      customer
    })
  );

  const columnsToUse = useMemo(() => {
    if (columnsAssistances) {
      return columnsForAssistance;
    }

    if (columnsCoachAndSeat) {
      return columnsForCoachAndSeat;
    }

    if (columnsTicket) {
      return columnsForTickets;
    }

    if (columnsLuggage) {
      return columnsForLuggage;
    }

    return columns;
  }, [columnsAssistances, columnsCoachAndSeat, columnsTicket, columnsLuggage]);

  const filteredColumns = columnsToUse.filter(({ key }) => !removeColumns.includes(key));

  return (
    <SortableTable
      formatHeader={({ key, intlId }) =>
        intlId ? <FormattedMessage id={intlId} /> : <FormattedMessage id={`bookingSearchResults.${key}`} />
      }
      borderColor={colours.Greyscale.CharcoalGrey}
      alternateBackground={colours.Messaging.SuccessGreenLight}
      data={data}
      columns={filteredColumns}
      sortKey={sortKey}
      onReorder={updateUrl}
      sortOrderIsAscending={sortOrderIsAscending}
    />
  );
};

BookingSearchTable.propTypes = {
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      reference: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      masterReference: PropTypes.string,
      paymentDue: PropTypes.string,
      minorsAmount: PropTypes.string,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      passengerCount: PropTypes.number.isRequired,
      departingFrom: PropTypes.string.isRequired,
      departingOn: PropTypes.string.isRequired,
      departingTo: PropTypes.string.isRequired,
      returningOn: PropTypes.string.isRequired
    })
  ).isRequired,
  columnsAssistances: PropTypes.bool,
  columnsCoachAndSeat: PropTypes.bool,
  columnsTicket: PropTypes.bool,
  columnsLuggage: PropTypes.bool,
  defaultSortKey: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired
  }).isRequired,
  removeColumns: PropTypes.arrayOf(PropTypes.string)
};

export default BookingSearchTable;
